import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import cacheManager from '../CacheManager';
import Select from 'react-select';
import { formatDate } from '../js/dateFormatter';
import { showSuccess, showError } from '../utils/toast';

const PgAgendamentoEditar = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);

    const [clientesById, setClientesById] = useState({});
    const [itensById, setItensById] = useState({});
    const [faturasById, setFaturasById] = useState({});

    const [clienteId, setClienteId] = useState('');
    const [itemId, setItemId] = useState('');
    const [faturaId, setFaturaId] = useState('');
    const [data, setData] = useState('');
    const [horaInicio, setHoraInicio] = useState('00');
    const [minutoInicio, setMinutoInicio] = useState('00');
    const [horaFim, setHoraFim] = useState('00');
    const [minutoFim, setMinutoFim] = useState('00');
    const [observacoes, setObservacoes] = useState('');
    const [valor, setValor] = useState('');
    const [horaInicioError, setHoraInicioError] = useState(false);
    const [horaFimError, setHoraFimError] = useState(false);

    const carregarCache = async () => {
        try {
            setIsLoading(true);
            const clientesById = await cacheManager.getClientesById();
            setClientesById(clientesById);
            const itensById = await cacheManager.getItensById();
            setItensById(itensById);
            const faturasById = await cacheManager.getFaturasById();
            setFaturasById(faturasById);
        } catch (error) {
            console.error('Erro ao buscar clientes:', error);
        } finally {
            setIsLoading(false);
        }
    }
    

    useEffect(() => {
        const carregarDados = async () => {
          await carregarCache();
          
          if (id) {
            setIsLoading(true);
            fetch(`https://api.faeworks.com.br/agendamento/read.php?id=${id}`)
              .then(response => response.json())
              .then(data => {
                if (data && data.length > 0) {
                  const agendamento = data[0];
                  setClienteId(agendamento.id_cliente);
                  setItemId(agendamento.id_item);
                  setFaturaId(agendamento.id_fatura);
                  
                  // Separar data e hora de data_inicio_agendamento
                  if (agendamento.data_inicio_agendamento) {
                    const dataInicio = new Date(agendamento.data_inicio_agendamento);
                    setData(dataInicio.toISOString().split('T')[0]);
                    const hora = String(dataInicio.getHours()).padStart(2, '0');
                    const minuto = String(dataInicio.getMinutes()).padStart(2, '0');
                    setHoraInicio(hora);
                    setMinutoInicio(minuto);
                    setHoraInicioError(parseInt(hora) < 8 || parseInt(hora) > 21);
                  }
                  
                  // Extrair hora de data_fim_agendamento
                  if (agendamento.data_fim_agendamento) {
                    const dataFim = new Date(agendamento.data_fim_agendamento);
                    const hora = String(dataFim.getHours()).padStart(2, '0');
                    const minuto = String(dataFim.getMinutes()).padStart(2, '0');
                    setHoraFim(hora);
                    setMinutoFim(minuto);
                    setHoraFimError(parseInt(hora) < 8 || parseInt(hora) > 21);
                  }
                  
                  setObservacoes(agendamento.observacoes);
                }
                else {
                  showError('Erro ao buscar dados do agendamento');
                }
              })
              .catch(error => console.error('Erro ao buscar dados do agendamento:', error))
              .finally(() => setIsLoading(false));
          }
        };
    
        carregarDados();
      }, [id]);

    const atualizarAgendamento = async () => {
    try {
        setIsLoading(true);
        
        // Garantir que as horas e minutos tenham 2 dígitos
        const horaInicioFormatada = String(horaInicio).padStart(2, '0');
        const minutoInicioFormatado = String(minutoInicio).padStart(2, '0');
        const horaFimFormatada = String(horaFim).padStart(2, '0');
        const minutoFimFormatado = String(minutoFim).padStart(2, '0');
        
        // Montar data_inicio_agendamento: YYYY-MM-DD HH:MM:00
        const dataInicioFormatada = `${data} ${horaInicioFormatada}:${minutoInicioFormatado}:00`;
        
        // Montar data_fim_agendamento: YYYY-MM-DD HH:MM:00
        const dataFimFormatada = `${data} ${horaFimFormatada}:${minutoFimFormatado}:00`;
        
        const url = id ? 'https://api.faeworks.com.br/agendamento/update.php' : 'https://api.faeworks.com.br/agendamento/create.php';
        const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            id_agendamento: id,
            id_cliente: clienteId,
            id_item: itemId,
            id_fatura: faturaId,
            data_inicio_agendamento: dataInicioFormatada,
            data_fim_agendamento: dataFimFormatada,
            observacoes: observacoes,
            valor: valor || (itensById[itemId]?itensById[itemId].preco:''),
        }),
        });
        const result = await response.json();
        if (result.status === 'success') {
            setIsLoading(false);
            showSuccess('Agendamento atualizado com sucesso!');
            cacheManager.invalidateAgendamentosCache();
            handleVoltarClick();
        } else {
            setIsLoading(false);
            showError('Erro ao atualizar agendamento: ' + result.message);
        }
    } catch (error) {
        console.error('Erro ao atualizar agendamento:', error);
        setIsLoading(false);
        showError('Erro ao atualizar agendamento.');
    }
    };

    // Estilos para esmaecer a página
    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 2,
        display: isLoading ? 'block' : 'none',
    };

    const handleObservacoesChange = (event) => {
        setObservacoes(event.target.value);
    };

    const handleValorChange = (event) => {
        setValor(event.target.value);
    };

    const handleVoltarClick = () => {
        navigate('/agendamentos');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        await atualizarAgendamento();
    };

    const handleClienteChange = (selectedOption) => {
        setClienteId(selectedOption ? selectedOption.value : '');
    };

    const clienteOptions = Object.keys(clientesById).map((key) => ({
        value: key,
        label: (clientesById[key].cpf ? clientesById[key].cpf + ' | ' : '') + clientesById[key].nome
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const handleItemChange = (selectedOption) => {
        setItemId(selectedOption ? selectedOption.value : '');
    };

    const itemOptions = Object.keys(itensById).map((key) => ({
        value: key,
        label: itensById[key].titulo + ' - ' + ((itensById[key].duracao/60)/60) + ' Horas - R$' + itensById[key].preco
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const handleFaturaChange = (selectedOption) => {
        setFaturaId(selectedOption ? selectedOption.value : '');
    };
    const faturaOptions = Object.keys(faturasById).filter((key) => {
        const dataEmissao = new Date(faturasById[key].data_emissao);
        const dataInicioAgendamento = new Date(data);
    
        const quatroMesesAntes = new Date(dataInicioAgendamento);
        quatroMesesAntes.setMonth(quatroMesesAntes.getMonth() - 4);
    
        const quatroMesesDepois = new Date(dataInicioAgendamento);
        quatroMesesDepois.setMonth(quatroMesesDepois.getMonth() + 4);
    
        let dataEmissaoOk = dataEmissao >= quatroMesesAntes && dataEmissao <= quatroMesesDepois;
        let mesmoClienteOk = String(faturasById[key].id_cliente) === String(clienteId);
        return dataEmissaoOk && mesmoClienteOk;
    })
    .map((key) => ({
        value: key,
        label: `#${String(faturasById[key].id_fatura).padStart(5, '0')} - ${formatDate(faturasById[key].data_emissao)} - R$${faturasById[key].valor_total} - ${clientesById[faturasById[key].id_cliente].nome}`
      }))
      .sort((a, b) => b.label.localeCompare(a.label));

return (
    <div className="container mt-5">
        {isLoading && <div style={overlayStyle}></div>} {/* Overlay de carregamento */}
        <h1 className="h1">{id ? 'Editar Agendamento' : 'Novo Agendamento'}</h1>
        <form onSubmit={handleSubmit} className="needs-validation" noValidate>
            <div className="row">
                <div className="col-12 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Cliente:
                        <Select
                        className="form-select"
                        value={clienteOptions.find(option => option.value === String(clienteId))}
                        onChange={handleClienteChange}
                        options={clienteOptions}
                        isClearable={false}
                        />
                    </label>
                </div>
                <div className="col-12 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Espaço:
                        <Select
                        className="form-select"
                        value={itemOptions.find(option => option.value === String(itemId))}
                        onChange={handleItemChange}
                        options={itemOptions}
                        isClearable={false}
                        />
                    </label>
                </div>
                <div className="col-4 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Data:
                        <input
                            type="date"
                            className="form-control"
                            value={data}
                            onChange={(event) => setData(event.target.value)}
                            required
                        />
                    </label>
                </div>
                <div className="col-2 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Hora Início:
                        <input
                            type="number"
                            className={`form-control ${horaInicioError ? 'is-invalid' : ''}`}
                            value={horaInicio}
                            onChange={(event) => {
                                const valor = event.target.value;
                                setHoraInicio(valor);
                                const hora = parseInt(valor);
                                setHoraInicioError(hora < 8 || hora > 21);
                            }}
                            min="0"
                            max="23"
                            placeholder="00"
                            required
                        />
                        {horaInicioError && (
                            <div className="invalid-feedback" style={{ display: 'block' }}>
                                Hora deve estar entre 8 e 21
                            </div>
                        )}
                    </label>
                </div>
                <div className="col-2 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Min Início:
                        <input
                            type="number"
                            className="form-control"
                            value={minutoInicio}
                            onChange={(event) => {
                                const valor = event.target.value;
                                setMinutoInicio(valor);
                            }}
                            min="0"
                            max="59"
                            placeholder="00"
                            required
                        />
                    </label>
                </div>
                <div className="col-2 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Hora Fim:
                        <input
                            type="number"
                            className={`form-control ${horaFimError ? 'is-invalid' : ''}`}
                            value={horaFim}
                            onChange={(event) => {
                                const valor = event.target.value;
                                setHoraFim(valor);
                                const hora = parseInt(valor);
                                setHoraFimError(hora < 8 || hora > 21);
                            }}
                            min="0"
                            max="23"
                            placeholder="00"
                            required
                        />
                        {horaFimError && (
                            <div className="invalid-feedback" style={{ display: 'block' }}>
                                Hora deve estar entre 8 e 21
                            </div>
                        )}
                    </label>
                </div>
                <div className="col-2 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Min Fim:
                        <input
                            type="number"
                            className="form-control"
                            value={minutoFim}
                            onChange={(event) => {
                                const valor = event.target.value;
                                setMinutoFim(valor);
                            }}
                            min="0"
                            max="59"
                            placeholder="00"
                            required
                        />
                    </label>
                </div>
                <div className="col-12 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Fatura:
                        <Select
                        className="form-select"
                        value={faturaOptions.find(option => option.value === String(faturaId))}
                        onChange={handleFaturaChange}
                        options={faturaOptions}
                        isClearable={true}
                        />
                    </label>
                </div>
            </div>
            <div className="row">
                <div className="col-6 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Valor:
                        <input
                                type="text"
                                className="form-control"
                                value={valor || (itensById[itemId]?itensById[itemId].preco:'')}
                                onChange={handleValorChange}
                                placeholder="00,00"
                                disabled={true}
                            />
                    </label>
                </div>
            </div>
            <div className="row">
                <div className="col-12 mb-3">
                    <label className="form-label" style={{ display: 'block' }}>
                        Observações:
                        <textarea
                            className="form-control"
                            value={observacoes || ''}
                            onChange={handleObservacoesChange}
                        ></textarea>
                    </label>
                </div>
            </div>
            <div className="row">
                <div className="col-12 d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={handleVoltarClick}
                    >
                        Voltar
                    </button>
                    <button type="submit" className="btn btn-primary">
                        Salvar
                    </button>
                </div>
            </div>
        </form>
    </div>
);
};

export default PgAgendamentoEditar;